/**
 * Clipboard Utilities
 * 
 * Helper functions for managing ClipboardJS instances and Tippy tooltips.
 * Used for copy-to-clipboard functionality in the shipment tracking interface.
 * 
 * @see https://clipboardjs.com/
 */

import * as ClipboardJS from 'clipboard';
import type { TippyInstance } from './tippy-init';

// Re-export ClipboardJS for use throughout the app
export { ClipboardJS };

// Export type for ClipboardJS instances
export type ClipboardInstance = InstanceType<typeof ClipboardJS>;

/**
 * Safely destroys a ClipboardJS instance and resets the global clipboard variable.
 * 
 * This function handles the cleanup of ClipboardJS event listeners to prevent
 * memory leaks when modals are closed or clipboard instances need to be recreated.
 * 
 * @param clipboardInstance - The ClipboardJS instance to destroy (optional, uses global if not provided)
 * 
 * @example
 * ```typescript
 * let clipboard = new ClipboardJS('.copy-btn');
 * // Later, when modal closes:
 * destroyClipboard(clipboard);
 * clipboard = null;
 * ```
 */
export function destroyClipboard(clipboardInstance?: ClipboardInstance | null): void {
  try {
    if (clipboardInstance) {
      clipboardInstance.destroy();
    }
  } catch (error) {
    console.warn('Error destroying clipboard:', error);
  }
}

/**
 * Displays a temporary tooltip message on a Tippy instance.
 * 
 * Used to show feedback when clipboard copy succeeds (e.g., "Copied!").
 * The tooltip automatically resets to default content after 2 seconds.
 * 
 * @param tooltipInstance - Tippy instance (or array of instances) to show message on
 * @param message - Message to display (defaults to "Copied!")
 * 
 * @example
 * ```typescript
 * const tooltip = tippy('#tracking_no', { content: 'Click to Copy' });
 * clipboard.on('success', () => {
 *   setTooltip(tooltip, 'Copied!');
 * });
 * ```
 */
export function setTooltip(
  tooltipInstance: TippyInstance | TippyInstance[] | null | undefined,
  message: string = 'Copied!'
): void {
  try {
    // Handle both array and single instance
    // tippy() returns either a single instance or an array
    const tooltip = Array.isArray(tooltipInstance) ? tooltipInstance[0] : tooltipInstance;

    if (tooltip) {
      tooltip.setContent(message);
      tooltip.show();

      setTimeout(() => {
        try {
          tooltip.hide();
          tooltip.setContent('Click to Copy');
        } catch (error) {
          console.warn('[Clipboard] Error resetting tooltip content:', error);
        }
      }, 2000);
    } else {
      console.warn('[Clipboard] Tooltip instance not available');
    }
  } catch (error) {
    console.error('[Clipboard] Error setting tooltip:', error);
  }
}
